import { ApiClient } from '../../../api/ApiClient';
import { v2 } from '../../../api/ApiInterfaceV2';
import { ResultData } from '../../../api/ApiTypes';
import { RequestTerminal } from '../../../api/RequestTerminal';
import { TaskPatchInput } from '../../../api/endpoints/tasks';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { PodRecordSet, RecordSetManager } from '../../../concepts/objects/RecordSetManager';
import { ActiveObject, resolve } from '../../Container';
import { BoardInfo } from '../../canvas/BoardInfo';
import { EditorSession } from '../../canvas/EditorSession';
import { BindingManager } from '../atlassian/BindingManager';
import { PodRecordBindingTarget } from '../atlassian/PodRecordBindingTarget';
import { Sticky } from '../paper/Sticky';
import { Assignable } from './Assignable';
import { TaskRecord, TaskRecordAttr } from './TaskAssocations';
import { tasksLoadInitial } from './TaskCommands';

const createPatchTerminalSubmit = () =>
    async (api: ApiClient, items: TaskPatchInput[]): Promise<ResultData> => {
        return api.exec(v2.tasks.patch({ items }));
    };

const createLoadTerminalSubmit = (board: BoardInfo) =>
    async (api: ApiClient, items: string[]): Promise<ResultData> => {
        // Dedupe
        items = Array.from(new Set(items));
        return api.exec(v2.tasks.load({ boardId: board.id, taskIds: items }));
    };


export class TaskRecordManager extends ActiveObject {

    @resolve(ApiClient) private api: ApiClient;
    @resolve(BindingManager) private bindings: BindingManager;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(ObjectManager) private objects: ObjectManager;
    @resolve(RecordSetManager) private recordSets: RecordSetManager;
    @resolve(EditorSession) private session!: EditorSession;

    private loadTerminal = null as RequestTerminal<string>;
    private patchTerminal = null as RequestTerminal<TaskPatchInput>;
    private recordSet: PodRecordSet<TaskRecord>;

    public start(): void {
        this.burden(
            this.loadTerminal = this.api.createTerminal(
                createLoadTerminalSubmit(this.session.get(BoardInfo))
            ),
            this.patchTerminal = this.api.createTerminal(
                createPatchTerminalSubmit()
            ),
            this.objects.on('Sticky', 'allocated', ([x]) => this.configureBinding(x.as(Sticky))),
            this.objects.on('Sticky', 'affected', ([x]) => this.configureBinding(x.as(Sticky))),
            this.objects.on('Sticky', 'released', ([x]) => this.releaseBinding(x.as(Sticky))),
        );

        this.recordSet = this.recordSets.resolve('Tasks');

        this.dispatch(tasksLoadInitial);
        this.configureInitialBindings();
    }

    public load(taskId: string) {
        this.loadTerminal.queueNext(taskId);
        return this.waitFor(taskId);
    }

    public patch(patch: Record<string, unknown>, targetIds: string[]) {

        const hits = this.getAssignmentsFromUnknownIds(targetIds);
        if (!hits.length) return;

        for (const ab of hits) {
            // Patch the local copy
            this.bindings.lookup(ab)?.mutate(patch);

            if (!!ab.taskId) {
                this.patchTerminal.queueNext({
                    id: ab.taskId,
                    ...patch,
                });
            }
        }
    }

    public waitFor(taskId: string) {
        return this.recordSet.waitForFuture(taskId);
    }

    private getAssignmentsFromUnknownIds(targetIds: string[]) {

        const hits = [] as Assignable[];
        const unknowns = this.objects.getMany(targetIds);

        for (const u of unknowns) {
            if (u.is(Assignable)) {
                hits.push(u.as(Assignable));
            }
        }

        return hits;
    }

    private configureInitialBindings() {
        const assignments = Array.from(this.objects.getAll(Assignable));
        for (const a of assignments) {
            this.configureBinding(a);
        }
    }

    private configureBinding(assigable: Assignable) {
        if (!!assigable.taskId) {
            const key = `record:${assigable.taskId}`;
            if (this.bindings.check(assigable, key)) return;
            const record = this.recordSet.getForFuture(assigable.taskId);
            this.bindings.bind(assigable, new PodRecordBindingTarget(key, record), TaskRecordAttr);
        }
        else {
            this.releaseBinding(assigable);
        }
    }

    private releaseBinding(assigable: Assignable) {
        this.bindings.unbind(assigable);
    }
}
